import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  CallScribeCallStatus,
  CallScribePipelineStage,
  CallScribeSentiment,
  ICallScribeAccessInfo,
  ICallScribeCallListItem,
  ICallScribeCallTranscript,
  ICallScribeParticipant,
  ICallScribeVoiceToken,
  ILiveTranscriptSegment,
} from 'services/callScribe/types';
import {
  compareLiveTranscriptSegments,
  liveTranscriptSegmentKey,
} from 'services/callScribe/transcriptOrder';
import { participantKey } from 'services/callScribe/participants';
import { ICallScribeMyDashboard } from 'services/callScribe/dashboard';
import {
  ThunkGetCallScribeAccess,
  ThunkGetCallScribeCall,
  ThunkGetCallScribeCallsByContact,
  ThunkGetCallScribeCallsByTicket,
  ThunkGetCallScribeMyDashboard,
  ThunkGetCallScribeTranscript,
  ThunkGetCallScribeVoiceToken,
  ThunkInitiateCallScribeCall,
  ThunkPostCallScribeTimeEntry,
  ThunkRejectCallScribeSuggestion,
  ThunkStopCallScribeRecording,
} from 'store/callScribe/thunks';

const TERMINAL_STATUSES = new Set<CallScribeCallStatus>([
  CallScribeCallStatus.Completed,
  CallScribeCallStatus.Failed,
  CallScribeCallStatus.NoAnswer,
  CallScribeCallStatus.Busy,
  CallScribeCallStatus.Cancelled,
]);

interface IInitialState {
  access: ICallScribeAccessInfo | undefined;
  accessLoading: boolean;
  accessError: string | undefined;
  voiceToken: ICallScribeVoiceToken | undefined;
  voiceTokenLoading: boolean;
  voiceTokenError: string | undefined;
  callsByTicket: ICallScribeCallListItem[];
  callsLoading: boolean;
  callsError: string | undefined;
  /** ID of an in-flight POST /calls (so the CallButton can show a spinner). */
  initiatingCall: boolean;
  /** ID of the call currently being acted on by the time-entry card. */
  postingTimeEntryFor: number | undefined;
  /** Per-call transcript cache, keyed by call id. `null` = fetched but not ready (404). */
  transcriptsByCallId: Record<number, ICallScribeCallTranscript | null>;
  /** Set of call ids whose transcript fetch is in flight. */
  transcriptLoadingFor: Record<number, boolean>;
  /** Per-tech dashboard projection (Sprint 7). `undefined` until first fetch resolves. */
  myDashboard: ICallScribeMyDashboard | undefined;
  myDashboardLoading: boolean;
  myDashboardError: string | undefined;
  pipelineByCallId: Record<number, { stage: CallScribePipelineStage; error: string | null }>;
  intelligenceEventCountByCallId: Record<number, number>;
  terminalStatusEventCount: number;
  answeredAtByCallId: Record<number, string>;
  recordingStoppedAtByCallId: Record<number, string>;
  isRecordingByCallId: Record<number, boolean>;
  liveTranscriptByCallId: Record<
    number,
    {
      committed: Record<string, ILiveTranscriptSegment>;
      partials: Record<string, ILiveTranscriptSegment | undefined>;
    }
  >;
  participantsByCallId: Record<number, ICallScribeParticipant[]>;
  participantPatchesByCallId: Record<
    number,
    Record<number, { at: number; label: string; nameSource: string | number }>
  >;
  protectedTranscriptCallId?: number;
  activeTranscriptCallId?: number;
}

const initialState: IInitialState = {
  access: undefined,
  accessLoading: false,
  accessError: undefined,
  voiceToken: undefined,
  voiceTokenLoading: false,
  voiceTokenError: undefined,
  callsByTicket: [],
  callsLoading: false,
  callsError: undefined,
  initiatingCall: false,
  postingTimeEntryFor: undefined,
  transcriptsByCallId: {},
  transcriptLoadingFor: {},
  myDashboard: undefined,
  myDashboardLoading: false,
  myDashboardError: undefined,
  pipelineByCallId: {},
  intelligenceEventCountByCallId: {},
  terminalStatusEventCount: 0,
  answeredAtByCallId: {},
  recordingStoppedAtByCallId: {},
  isRecordingByCallId: {},
  liveTranscriptByCallId: {},
  participantsByCallId: {},
  participantPatchesByCallId: {},
  protectedTranscriptCallId: undefined,
  activeTranscriptCallId: undefined,
};

const callScribeSlice = createSlice({
  name: 'callScribe',
  initialState,
  reducers: {
    applyStatusEvent (
      state,
      action: PayloadAction<{
        callId: number;
        status: CallScribeCallStatus;
        answeredAtUtc: string | null;
        endedAtUtc: string | null;
        durationSeconds: number | null;
        isRecording?: boolean | null;
        recordingStoppedAtUtc?: string | null;
      }>,
    ) {
      if (TERMINAL_STATUSES.has(action.payload.status)) {
        state.terminalStatusEventCount += 1;
      }
      if (action.payload.answeredAtUtc !== null) {
        state.answeredAtByCallId[action.payload.callId] = action.payload.answeredAtUtc;
      }
      if (typeof action.payload.isRecording === 'boolean') {
        state.isRecordingByCallId[action.payload.callId] = action.payload.isRecording;
      }
      if (action.payload.recordingStoppedAtUtc) {
        state.recordingStoppedAtByCallId[action.payload.callId] =
          action.payload.recordingStoppedAtUtc;
      }
      const idx = state.callsByTicket.findIndex(c => c.id === action.payload.callId);
      if (idx < 0) return;
      const row = state.callsByTicket[idx];
      row.status = action.payload.status;
      if (action.payload.answeredAtUtc !== null) row.answeredAtUtc = action.payload.answeredAtUtc;
      if (action.payload.endedAtUtc !== null) row.endedAtUtc = action.payload.endedAtUtc;
      if (action.payload.durationSeconds !== null) row.durationSeconds = action.payload.durationSeconds;
    },
    applyIntelligenceEvent (
      state,
      action: PayloadAction<{
        callId: number;
        summaryShort: string | null;
        sentiment: CallScribeSentiment;
      }>,
    ) {
      const callId = action.payload.callId;
      state.intelligenceEventCountByCallId[callId] =
        (state.intelligenceEventCountByCallId[callId] ?? 0) + 1;
      const idx = state.callsByTicket.findIndex(c => c.id === callId);
      if (idx < 0) return;
      const row = state.callsByTicket[idx];
      row.hasIntelligence = true;
      if (action.payload.summaryShort !== null) row.summaryShort = action.payload.summaryShort;
      row.sentiment = action.payload.sentiment;
    },
    applyPipelineEvent (
      state,
      action: PayloadAction<{
        callId: number;
        stage: CallScribePipelineStage;
        error: string | null;
      }>,
    ) {
      state.pipelineByCallId[action.payload.callId] = {
        stage: action.payload.stage,
        error: action.payload.error,
      };
      const idx = state.callsByTicket.findIndex(c => c.id === action.payload.callId);
      if (idx >= 0) {
        state.callsByTicket[idx].pipelineStage = action.payload.stage;
        state.callsByTicket[idx].pipelineError = action.payload.error;
      }
    },
    applyTranscriptSegmentEvent (
      state,
      action: PayloadAction<{ callId: number; segment: ILiveTranscriptSegment }>,
    ) {
      const { callId, segment } = action.payload;
      const entry = state.liveTranscriptByCallId[callId] ?? { committed: {}, partials: {} };
      if (segment.committed) {
        entry.committed[liveTranscriptSegmentKey(segment)] = segment;
        delete entry.partials[participantKey(segment)];
        const values = Object.values(entry.committed);
        if (values.length > 400) {
          values
            .sort(compareLiveTranscriptSegments)
            .slice(0, values.length - 400)
            .forEach(s => { delete entry.committed[liveTranscriptSegmentKey(s)]; });
        }
      } else {
        entry.partials[participantKey(segment)] = segment;
      }
      state.liveTranscriptByCallId[callId] = entry;
    },
    applyParticipantsEvent (
      state,
      action: PayloadAction<{ callId: number; participants: ICallScribeParticipant[] }>,
    ) {
      state.participantsByCallId[action.payload.callId] = action.payload.participants;
    },
    upsertParticipant (
      state,
      action: PayloadAction<{ callId: number; participant: ICallScribeParticipant }>,
    ) {
      const { callId, participant } = action.payload;
      const list = state.participantsByCallId[callId] ?? [];
      const at = list.findIndex(p => p.id === participant.id);
      state.participantsByCallId[callId] = at >= 0
        ? list.map((p, i) => (i === at ? { ...p, ...participant } : p))
        : [...list, participant];
    },
    applyFetchedParticipants (
      state,
      action: PayloadAction<{
        callId: number;
        participants: ICallScribeParticipant[];
        requestedAt: number;
      }>,
    ) {
      const { callId, participants, requestedAt } = action.payload;
      const patches = state.participantPatchesByCallId[callId] ?? {};
      state.participantsByCallId[callId] = participants.map(p => {
        const patch = patches[p.id];
        return patch && patch.at >= requestedAt
          ? { ...p, label: patch.label, nameSource: patch.nameSource }
          : p;
      });
    },
    patchParticipant (
      state,
      action: PayloadAction<{
        callId: number;
        participantId: number;
        label: string;
        nameSource: string | number;
        at: number;
        fallback?: ICallScribeParticipant[];
      }>,
    ) {
      const { callId, participantId, label, nameSource, at, fallback } = action.payload;
      const list = state.participantsByCallId[callId] ?? fallback;
      if (list) {
        state.participantsByCallId[callId] = list.map(p =>
          p.id === participantId ? { ...p, label, nameSource } : p,
        );
      }
      state.participantPatchesByCallId[callId] = {
        ...(state.participantPatchesByCallId[callId] ?? {}),
        [participantId]: { at, label, nameSource },
      };
    },
    clearVoiceToken (state) {
      state.voiceToken = undefined;
      state.voiceTokenError = undefined;
    },
    resetLiveTranscripts (state) {
      state.liveTranscriptByCallId = {};
      state.participantsByCallId = {};
      state.participantPatchesByCallId = {};
      state.pipelineByCallId = {};
      state.protectedTranscriptCallId = undefined;
    },
    mergeTranscriptSegments (
      state,
      action: PayloadAction<{ callId: number; segments: ILiveTranscriptSegment[] }>,
    ) {
      const { callId, segments } = action.payload;
      if (segments.length === 0) {
        return;
      }
      const entry = state.liveTranscriptByCallId[callId] ?? { committed: {}, partials: {} };
      segments.forEach(segment => {
        entry.committed[liveTranscriptSegmentKey(segment)] = { ...segment, committed: true };
      });
      const values = Object.values(entry.committed);
      if (values.length > 400) {
        const kept = values.sort(compareLiveTranscriptSegments).slice(values.length - 400);
        entry.committed = Object.fromEntries(
          kept.map(segment => [liveTranscriptSegmentKey(segment), segment]),
        );
      }
      state.liveTranscriptByCallId[callId] = entry;
    },
    observeAnsweredAt (
      state,
      action: PayloadAction<{ callId: number; answeredAtUtc: string }>,
    ) {
      if (state.answeredAtByCallId[action.payload.callId] === undefined) {
        state.answeredAtByCallId[action.payload.callId] = action.payload.answeredAtUtc;
      }
    },
    setActiveTranscriptCall (state, action: PayloadAction<number>) {
      state.activeTranscriptCallId = action.payload;
      const keep = new Set<number>([action.payload]);
      if (typeof state.protectedTranscriptCallId === 'number') {
        keep.add(state.protectedTranscriptCallId);
      }
      Object.keys(state.liveTranscriptByCallId)
        .filter(key => !keep.has(Number(key)))
        .forEach(key => { delete state.liveTranscriptByCallId[Number(key)]; });
      Object.keys(state.participantsByCallId)
        .filter(key => !keep.has(Number(key)))
        .forEach(key => { delete state.participantsByCallId[Number(key)]; });
      Object.keys(state.participantPatchesByCallId)
        .filter(key => !keep.has(Number(key)))
        .forEach(key => { delete state.participantPatchesByCallId[Number(key)]; });
    },
    protectTranscriptCall (state, action: PayloadAction<number>) {
      state.protectedTranscriptCallId = action.payload;
    },
    releaseTranscriptCall (state) {
      state.protectedTranscriptCallId = undefined;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(ThunkGetCallScribeAccess.pending, state => {
        state.accessLoading = true;
        state.accessError = undefined;
      })
      .addCase(ThunkGetCallScribeAccess.fulfilled, (state, action) => {
        state.access = action.payload;
        state.accessLoading = false;
        state.accessError = undefined;
      })
      .addCase(ThunkGetCallScribeAccess.rejected, state => {
        // A failed request is not a denial: reporting hasAccess false here told the
        // technician the feature was off and hid the nav item until a reload.
        state.accessLoading = false;
        state.accessError = 'Could not check whether CallScribe is available.';
      });

    builder
      .addCase(ThunkGetCallScribeVoiceToken.pending, state => {
        state.voiceTokenLoading = true;
        state.voiceTokenError = undefined;
      })
      .addCase(ThunkGetCallScribeVoiceToken.fulfilled, (state, action) => {
        state.voiceToken = action.payload;
        state.voiceTokenLoading = false;
      })
      .addCase(ThunkGetCallScribeVoiceToken.rejected, (state, action) => {
        state.voiceTokenLoading = false;
        state.voiceTokenError = action.error.message ?? 'Failed to mint voice token';
        state.voiceToken = undefined;
      });

    builder
      .addCase(ThunkGetCallScribeCallsByTicket.pending, state => {
        state.callsLoading = true;
        state.callsError = undefined;
      })
      .addCase(ThunkGetCallScribeCallsByTicket.fulfilled, (state, action) => {
        state.callsError = undefined;
        state.callsByTicket = action.payload;
        state.callsLoading = false;
      })
      .addCase(ThunkGetCallScribeCallsByTicket.rejected, state => {
        state.callsLoading = false;
        state.callsError = 'Could not load the calls for this ticket.';
      });

    builder
      .addCase(ThunkGetCallScribeCallsByContact.pending, state => {
        state.callsLoading = true;
        state.callsError = undefined;
      })
      .addCase(ThunkGetCallScribeCallsByContact.fulfilled, (state, action) => {
        state.callsError = undefined;
        state.callsByTicket = action.payload;
        state.callsLoading = false;
      })
      .addCase(ThunkGetCallScribeCallsByContact.rejected, state => {
        state.callsLoading = false;
        state.callsError = 'Could not load the calls for this contact.';
      });

    builder
      .addCase(ThunkInitiateCallScribeCall.pending, state => {
        state.initiatingCall = true;
      })
      .addCase(ThunkInitiateCallScribeCall.fulfilled, (state, action) => {
        state.initiatingCall = false;
        state.callsByTicket.unshift(action.payload);
      })
      .addCase(ThunkInitiateCallScribeCall.rejected, state => {
        state.initiatingCall = false;
      });

    builder
      .addCase(ThunkPostCallScribeTimeEntry.pending, (state, action) => {
        state.postingTimeEntryFor = action.meta.arg.callId;
      })
      .addCase(ThunkPostCallScribeTimeEntry.fulfilled, (state, action) => {
        const callId = action.meta.arg.callId;
        const idx = state.callsByTicket.findIndex(c => c.id === callId);
        if (idx >= 0) {
          state.callsByTicket[idx].linkStatus = action.payload.status;
          state.callsByTicket[idx].linkPostedAtUtc = action.payload.postedAtUtc ?? null;
          state.callsByTicket[idx].linkErrorMessage = action.payload.errorMessage ?? null;
        }
        // Live dashboard sync: a successful time-entry post means one fewer
        // unfinished follow-up. Clamp at 0 so a stale dashboard never goes negative.
        if (state.myDashboard && state.myDashboard.unfinishedFollowUpsCount > 0) {
          state.myDashboard.unfinishedFollowUpsCount -= 1;
        }
        state.postingTimeEntryFor = undefined;
      })
      .addCase(ThunkPostCallScribeTimeEntry.rejected, state => {
        state.postingTimeEntryFor = undefined;
      });

    builder.addCase(ThunkStopCallScribeRecording.fulfilled, (state, action) => {
      const callId = action.meta.arg;
      const stoppedAt = action.payload?.recordingStoppedAtUtc ?? null;
      state.isRecordingByCallId[callId] = false;
      if (stoppedAt) {
        state.recordingStoppedAtByCallId[callId] = stoppedAt;
      }
      const idx = state.callsByTicket.findIndex(c => c.id === callId);
      if (idx >= 0 && action.payload) {
        state.callsByTicket[idx] = { ...state.callsByTicket[idx], ...action.payload };
      }
    });

    builder.addCase(ThunkRejectCallScribeSuggestion.fulfilled, (state, action) => {
      const callId = action.meta.arg;
      const idx = state.callsByTicket.findIndex(c => c.id === callId);
      if (idx >= 0) {
        state.callsByTicket[idx].linkStatus = action.payload.status;
      }
      // A rejection also clears the follow-up from the tech's queue.
      if (state.myDashboard && state.myDashboard.unfinishedFollowUpsCount > 0) {
        state.myDashboard.unfinishedFollowUpsCount -= 1;
      }
    });

    builder.addCase(ThunkGetCallScribeCall.fulfilled, (state, action) => {
      const row = action.payload;
      if (!row) return;
      const idx = state.callsByTicket.findIndex(c => c.id === row.id);
      if (idx >= 0) {
        state.callsByTicket[idx] = row;
      }
    });

    builder
      .addCase(ThunkGetCallScribeTranscript.pending, (state, action) => {
        state.transcriptLoadingFor[action.meta.arg] = true;
      })
      .addCase(ThunkGetCallScribeTranscript.fulfilled, (state, action) => {
        const callId = action.meta.arg;
        // payload === null means the BE returned 404 — transcription not ready yet.
        // Storing null lets the UI distinguish "checked, still processing" from
        // "never asked" without an extra flag.
        state.transcriptsByCallId[callId] = action.payload;
        state.transcriptLoadingFor[callId] = false;
      })
      .addCase(ThunkGetCallScribeTranscript.rejected, (state, action) => {
        state.transcriptLoadingFor[action.meta.arg] = false;
      });

    builder
      .addCase(ThunkGetCallScribeMyDashboard.pending, state => {
        state.myDashboardLoading = true;
        state.myDashboardError = undefined;
      })
      .addCase(ThunkGetCallScribeMyDashboard.fulfilled, (state, action) => {
        state.myDashboard = action.payload;
        state.myDashboardLoading = false;
      })
      .addCase(ThunkGetCallScribeMyDashboard.rejected, (state, action) => {
        state.myDashboardLoading = false;
        state.myDashboardError = action.error.message ?? 'Failed to load dashboard';
      });
  },
});

export const {
  applyStatusEvent,
  applyIntelligenceEvent,
  applyPipelineEvent,
  applyTranscriptSegmentEvent,
  applyParticipantsEvent,
  upsertParticipant,
  applyFetchedParticipants,
  patchParticipant,
  clearVoiceToken,
  resetLiveTranscripts,
  setActiveTranscriptCall,
  mergeTranscriptSegments,
  observeAnsweredAt,
  protectTranscriptCall,
  releaseTranscriptCall,
} = callScribeSlice.actions;

export default callScribeSlice.reducer;
